import { takeEvery, put, delay, call } from "redux-saga/effects";
import { types } from "../reducers/analysis";
import { AnalysisService } from "../../services";
import { dds, ddsData, recommendations } from '../../helpers/hardcoded/sca';
import { mapScl } from "../../helpers/dataMapping/scl";
import { isProxy } from 'datastake-daf/src/helpers/Token';
import { PROJECTS } from "../../helpers/CanAccess";
import i18next from "i18next";
import AmpService from "../../services/AmpService";
import ProgramsService from "../../services/ProgramsService";

const allTabs = {
  checks: {
    label: 'Counterparty Review',
  },
  documentationChecks: {
    label: 'Documentation Review',
  },
  managementSystems: {
    label: 'Management Systems',
  },
  challenges: {
    label: 'Challenges',
  },
};

const getTableComponents = (_data, challengeOptions = []) => {
  const lng = i18next.language;
  const tabs = {};
  const newData = {};
  const isMulti = false;
  const showManagement = false;

  const challenges = [];

  [...(_data?.checks || []), ...(_data?.documentationChecks || [])].forEach((check) => {
    const { challengeCategory, challengeText, challengeSeverity, challenge, updatedAt, title } = check;

    if (challenge && challengeCategory) {
      const challangeIndex = challenges.findIndex((v) => v.category === challengeCategory);
      if (challangeIndex !== -1) {
        challenges[challangeIndex].checks.push({
          criteria: challengeText,
          severity: challengeSeverity,
          title,
          updatedAt,
        });
      } else {
        const titleObj = challengeOptions[challengeCategory];
        const titleLabel = titleObj ? titleObj[lng] || titleObj.en || challengeCategory : challengeCategory;

        challenges.push({
          title: titleLabel,
          category: challengeCategory,
          checks: [
            {
              criteria: challengeText,
              title,
              severity: challengeSeverity,
              updatedAt,
            },
          ],
        });
      }
    }
  });

  const data = { ..._data, challenges };


  Object.keys(allTabs).forEach((k) => {
    if (k === 'managementSystems' && showManagement) {
      data[k] = data.documentationChecks
    }

    if (data[k]) {
      tabs[k] = allTabs[k];
    }
  });

  Object.keys(allTabs).forEach((k) => {
    if (k === 'managementSystems' && showManagement) {
      data[k] = data.documentationChecks
    }

    if (data[k]) {
      if (k === 'challenges') {
        data[k] = data[k].filter((d) => (d.checks || []).length > 0);
      }

      if (isMulti && (k === 'checks' || k === 'documentationChecks' || k === 'managementSystems')) {
        data[k] = data[k].map((d, i) => i % 2 === 0 ? ({
          ...d,
          sources: [{
            label: 'Justice Plus',
            ...d,
            sources: null,
          }]
        }) : ({
          ...d,
          sources: [
            {
              label: 'Justice Plus',
              ...d,
              sources: null,
            },
            {
              label: 'Better Chain',
              ...d,
              sources: null,
            },
          ]
        }))
      }
      newData[k] = data[k];
    }
  });

  return {
    tabs,
    data: newData,
  };
};

const badOutcomes = [
  'negativeUnverified',
  'undeterminedSubject',
  'undetermined',
  'negativeVerified',
  'notApplicable',
  'unavailable',
];

const getRatingPercentage = (tableComponents) => {
  const docCompleted = (tableComponents.data.documentationChecks || []).filter(d => d.outcome && d.outcome.status && !badOutcomes.includes(d.outcome.status)).length;
  const totalDocs = (tableComponents.data.documentationChecks || []).length;
  return {
    assessment: 0,
    riskReadiness: 0,
    documentation: parseInt(((docCompleted / totalDocs) * 100).toFixed(0), 10),
    challanges: (tableComponents.data.challenges || []).reduce((total, ch) => total + (ch.checks || []).length, 0),
  }
}
const getCheckReviews = ({ selectedSourceId, selectedProject, documentationChecks = [], checks = [], challanges }) => {
  const totalChecks = documentationChecks.length + checks.length;
  const docCompleted = (documentationChecks || []).filter(d => d.outcome && d.outcome.status && !badOutcomes.includes(d.outcome.status)).length;
  const checkCompleted = (checks || []).filter(d => d.outcome && d.outcome === 'comply').length;
  const totalChallages = selectedSourceId || selectedProject === 'myOwnData' ? (totalChecks) : (checks.length + documentationChecks.length);
  const totalCompleted = checkCompleted + docCompleted;
  const verifiedChecks = parseFloat(((checks || []).filter(d => d.consolidation && d.consolidation === 'verified').length).toFixed(2));
  return selectedSourceId || selectedProject === 'myOwnData' ? {
    totalChecks,
    determination: parseFloat((totalCompleted / totalChecks).toFixed(2)) * 100,
    resolved: totalChallages - challanges,
    active: challanges,
  } : {
    totalChecks,
    determination: parseFloat((totalCompleted / totalChecks).toFixed(2)) * 100,
    triangulation: (checks || []).filter(d => d.authors && d.authors > 1).length,
    consensus: verifiedChecks,
    resolved: totalChallages - challanges,
    active: challanges,
  }
}

function* getScp({ value: { selectedSourceId, selectedProject, id, defaultSources } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis(id, {
      project: selectedProject,
      sourceId: selectedSourceId ? [selectedSourceId] : defaultSources ? defaultSources : [],
    }));
    if (res.status === 200) {
      const isEmpty = Object.values(res.data.widgets)
        .filter((v) => typeof v === 'object')
        .every((v) => Object.keys(v).length === 0);
      if (isEmpty) {
        throw new Error('Empty');
      }
      const tableComponents = getTableComponents((selectedProject !== 'myOwnData' && !selectedSourceId) ? { ...res.data, recommendations } : res.data, res?.data?.options?.challengeCategoryOptions);
      const counterpartyRating = getRatingPercentage(tableComponents)
      yield put({
        type: types.SCP_FETCH_SUCCESS,
        sources: res.data.sources,
        widgets: {
          ...res.data.widgets,
          dds: ddsData,
          counterpartyScore: res.data.counterpartyScore,
          kycScore: res.data.kycScore,
          riskRediness: res.data.riskRediness,
          documentation: res.data.documentation,
          counterpartyRating,
          tableComponents,
          checksReview: getCheckReviews({
            selectedSourceId,
            selectedProject,
            checks: res.data.checks || [],
            documentationChecks: res.data.documentationChecks || [],
            challanges: counterpartyRating.challanges,
          }),
        },
        options: res.data.options,
      });
    }
  } catch (err) {
    if (isProxy() && sessionStorage.getItem('proxy_project') !== PROJECTS.myData) {
      sessionStorage.setItem('proxy_project', PROJECTS.myData);
      window.location.reload();
      return;
    }
    console.log(err);
    yield put({ type: types.SCP_FETCH_FAILED });
  }
}

function* getScpSources({ value: { selectedSourceId, selectedProject, id, language, sources, callback } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis(id, {
      project: selectedProject,
      language,
      sourceId: sources,
    }));
    if (res.status === 200) {
      const tableComponents = getTableComponents((selectedProject !== 'myOwnData' && !selectedSourceId) ? { ...res.data, recommendations } : res.data, res?.data?.options?.challengeCategoryOptions);
      const counterpartyRating = getRatingPercentage(tableComponents)
      yield put({
        type: types.SCP_SOURCE_CHANGED_SUCCESS,
        sources: res.data.sources,
        widgets: {
          ...res.data.widgets,
          dds: ddsData,
          counterpartyScore: res.data.counterpartyScore,
          kycScore: res.data.kycScore,
          riskRediness: res.data.riskRediness,
          documentation: res.data.documentation,
          counterpartyRating,
          tableComponents,
          checksReview: getCheckReviews({
            selectedSourceId,
            selectedProject,
            checks: res.data.checks || [],
            documentationChecks: res.data.documentationChecks || [],
            challanges: counterpartyRating.challanges,
          }),

        },
        options: res.data.options,
      });
    }
  } catch (err) {
    yield put({ type: types.SCP_FETCH_FAILED });
  }
  if (typeof callback === 'function') {
    callback();
  }
}

function* getMineOperators({ value: { selectedSourceId, selectedProject, id, defaultSources } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis(id, {
      project: selectedProject,
      sourceId: selectedSourceId ? [selectedSourceId] : defaultSources ? defaultSources : [],
    }));
    if (res.status === 200) {
      const isEmpty = Object.values(res.data.widgets)
        .filter((v) => typeof v === 'object')
        .every((v) => Object.keys(v).length === 0);
      if (isEmpty) {
        throw new Error('Empty');
      }
      const tableComponents = getTableComponents((selectedProject !== 'myOwnData' && !selectedSourceId) ? { ...res.data, recommendations } : res.data, res?.data?.options?.challengeCategoryOptions);
      const counterpartyRating = getRatingPercentage(tableComponents)
      yield put({
        type: types.MINE_OPERATORS_FETCH_SUCCESS,
        sources: res.data.sources,
        widgets: {
          ...res.data.widgets,
          dds: ddsData,
          counterpartyScore: res.data.counterpartyScore,
          kycScore: res.data.kycScore,
          riskRediness: res.data.riskRediness,
          documentation: res.data.documentation,
          counterpartyRating,
          tableComponents,
          checksReview: getCheckReviews({
            selectedSourceId,
            selectedProject,
            checks: res.data.checks || [],
            documentationChecks: res.data.documentationChecks || [],
            challanges: counterpartyRating.challanges,
          }),
        },
        options: res.data.options,
      });
    }
  } catch (err) {
    if (isProxy() && sessionStorage.getItem('proxy_project') !== PROJECTS.myData) {
      sessionStorage.setItem('proxy_project', PROJECTS.myData);
      window.location.reload();
      return;
    }
    console.log(err);
    yield put({ type: types.MINE_OPERATORS_FETCH_FAILED });
  }
}

function* getMineOperatorsSources({ value: { selectedSourceId, selectedProject, id, language, sources, callback } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis(id, {
      project: selectedProject,
      language,
      sourceId: sources,
    }));
    if (res.status === 200) {
      const tableComponents = getTableComponents((selectedProject !== 'myOwnData' && !selectedSourceId) ? { ...res.data, recommendations } : res.data, res?.data?.options?.challengeCategoryOptions);
      const counterpartyRating = getRatingPercentage(tableComponents)
      yield put({
        type: types.MINE_OPERATORS_SOURCE_CHANGED_SUCCESS,
        sources: res.data.sources,
        widgets: {
          ...res.data.widgets,
          dds: ddsData,
          counterpartyScore: res.data.counterpartyScore,
          kycScore: res.data.kycScore,
          riskRediness: res.data.riskRediness,
          documentation: res.data.documentation,
          counterpartyRating,
          tableComponents,
          checksReview: getCheckReviews({
            selectedSourceId,
            selectedProject,
            checks: res.data.checks || [],
            documentationChecks: res.data.documentationChecks || [],
            challanges: counterpartyRating.challanges,
          }),

        },
        options: res.data.options,
      });
    }
  } catch (err) {
    yield put({ type: types.MINE_OPERATORS_FETCH_FAILED });
  }
  if (typeof callback === 'function') {
    callback();
  }
}

function* getAmp({ value: { selectedSourceId, selectedProject, id, defaultSources } }) {
  try {
    //     const { data } = yield call(() => AmpService.getOne(id));
    //     const _stakeholder = data.linking.STAKEHOLDER[data.stakeholder];

    //     yield put({
    //       type: types.AMP_FETCH_SUCCESS,
    //       sources: [],
    //       widgets: {
    //         subjectDetails: {
    //           subjectName: _stakeholder.name,
    //         },
    //         meetPeople: {
    //           description: '',
    //           managers: '',
    //           registered: '',
    //           safety: '',
    //           growth: '',
    //           focus: '',
    //           partners: '',
    //         },
    //         overview: {
    //           documents: [],
    //           images: [
    //             {
    //               "type": "image",
    //               "title": "Amani Bethlem_Fev 2021_2.jpeg",
    //               "url": "https://d6emlwyqv3muw.cloudfront.net/9ec4e24e-1e40-43c9-860f-82f617b8e3e3.jpeg",
    //               "source": "6f9142ba-3b99-4506-b4da-172a6cf8b591"
    //             },
    //           ],
    //           "name": "Migori | Kenya",
    //           "locationName": "Democratic Republic of the Congo / Ituri / Djugu",
    //           "products": [
    //             "Gold"
    //           ],
    //           "operator": "Antelope Mining",
    //           "legalForm": "",
    //           "finance": "Compressor and working capital",
    //           "score": 3.348809065934066,
    //           "coordinates": {
    //             "lat": -1.0006375951349356,
    //             "lng": 34.32613637557811
    //           }
    //         },
    //         keyIndicators: {
    //           workers: '10',
    //           production: { weight: '350g', per: 'quarter' },
    //           members: '',
    //           investment: '$19,500',
    //         },

    //       },
    //       options: [],
    //     });
    //   } catch (err) {
    //     console.log(err);
    //   }
    //   return;

    const { data } = yield call(() => AnalysisService.getAmpAnalysis(id));

    yield put({
      type: types.AMP_FETCH_SUCCESS,
      sources: [],
      widgets: {
        subjectDetails: {
          subjectName: data.subjectName,
        },
        ...data,
      },
      options: [],
    });
  } catch (err) {
    if (isProxy() && sessionStorage.getItem('proxy_project') !== PROJECTS.myData) {
      sessionStorage.setItem('proxy_project', PROJECTS.myData);
      window.location.reload();
      return;
    }
    console.log(err);
    yield put({ type: types.AMP_FETCH_FAILED });
  }
}

function* getAmpSources({ value: { selectedSourceId, selectedProject, id, language, sources, callback } }) {
  try {
    yield put({
      type: types.AMP_SOURCE_CHANGED_SUCCESS,
      sources: [],
      widgets: {
      },
      options: [],
    });
  } catch (err) {
    yield put({ type: types.MINE_OPERATORS_FETCH_FAILED });
  }
  if (typeof callback === 'function') {
    callback();
  }
}

function* getPrograms({ value: { selectedSourceId, selectedProject, id, defaultSources } }) {
  try {
    try {
      const { data } = yield call(() => ProgramsService.getAnalysis(id));

      yield put({
        type: types.PROGRAMS_FETCH_SUCCESS,
        sources: [],
        widgets: {
          ...data,
          subjectDetails: {
            subjectName: data.name,
          },
        },
      });
    } catch (err) {
      console.log(err);
    }
  } catch (err) {
    if (isProxy() && sessionStorage.getItem('proxy_project') !== PROJECTS.myData) {
      sessionStorage.setItem('proxy_project', PROJECTS.myData);
      window.location.reload();
      return;
    }
    console.log(err);
    yield put({ type: types.PROGRAMS_FETCH_FAILED });
  }
}

function* getProgramsSources({ value: { selectedSourceId, selectedProject, id, language, sources, callback } }) {
  try {
    yield put({
      type: types.PROGRAMS_SOURCE_CHANGED_SUCCESS,
      sources: [],
      widgets: {
      },
      options: [],
    });
  } catch (err) {
    yield put({ type: types.PROGRAMS_FETCH_FAILED });
  }
  if (typeof callback === 'function') {
    callback();
  }
}

function* getSclSources({ value: { selectedProject, id, language, sources, callback } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis(id, {
      project: selectedProject,
      language,
      sourceId: sources,
    }));
    if (res.status === 200) {
      // let associatedEvents = [];

      // const eventsRes = yield call(() => RiskService.getEvents({
      //   id: res.data.subjectDetails.subjectId,
      //   params: { sourceId: sources },
      // }));

      // if (eventsRes) {
      //   associatedEvents = eventsRes.data;
      // }

      const data = {
        widgets: {
          // ...mapScl(res.data, language, eventsRes.data, associatedEvents),
          ...mapScl(res.data, language),
          subjectId: res.data.subjectDetails.subjectId,
        },
        options: res.data.options,
        sources: res.data.sources || [],
      };
      yield put({
        type: types.SCL_SOURCE_CHANGED_SUCCESS,
        ...data,
      });
    }
  } catch (err) {
    yield put({ type: types.SCP_FETCH_FAILED });
  }
  if (typeof callback === 'function') {
    callback();
  }
}

function* getKyc({ value: { selectedProject, selectedSourceId } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis('kyc'));
    if (res.status === 200) {
      const isEmpty = Object.values(res.data.widgets)
        .filter((v) => typeof v === 'object')
        .every((v) => Object.keys(v).length === 0);
      if (isEmpty) {
        throw new Error('Empty');
      }
      const tableComponents = getTableComponents(res.data, res?.data?.options?.challengeCategoryOptions);
      const counterpartyRating = getRatingPercentage(tableComponents)
      yield put({
        type: types.KYC_FETCH_SUCCESS,
        sources: res.data.sources,
        options: res.data.options,
        widgets: {
          ...res.data.widgets,
          dds: ddsData,
          counterpartyScore: res.data.counterpartyScore,
          kycScore: res.data.kycScore,
          riskRediness: res.data.riskRediness,
          documentation: res.data.documentation,
          counterpartyRating,
          tableComponents,
          checksReview: getCheckReviews({
            selectedSourceId,
            selectedProject,
            checks: res.data.checks || [],
            documentationChecks: res.data.documentationChecks || [],
            challanges: counterpartyRating.challanges,
          }),
        },
      });
    } else {
      throw new Error('Error');
    }
  } catch (err) {
    yield put({ type: types.KYC_FETCH_FAILED });
  }
}
/*selectedSourceId, selectedProject, pmeSelectedSource, mod, defaultSources, id, language*/
function* getDD() {
  try {
  } catch (err) {
    console.log(err);
  }
  yield delay(1000);
  yield put({ type: types.DD_FETCH_SUCCESS, widgets: dds.widgets, options: dds.options });
}

function* getWidgetScp({ value: { widget, options } }) {
  yield delay(200);
  yield put({ type: types.SCP_GET_WIDGET_SUCCESS, data: ddsData.items[options.active], widget, });
}

function* getWidgetMineOperators({ value: { widget, options } }) {
  yield delay(200);
  yield put({ type: types.MINE_OPERATORS_GET_WIDGET_SUCCESS, data: ddsData.items[options.active], widget, });
}

function* getWidgetAmp({ value: { widget, options } }) {
  yield delay(200);
  yield put({ type: types.AMP_GET_WIDGET_SUCCESS, data: ddsData.items[options.active], widget, });
}

function* getWidgetPrograms({ value: { widget, options } }) {
  yield delay(200);
  yield put({ type: types.PROGRAMS_GET_WIDGET_SUCCESS, data: ddsData.items[options.active], widget, });
}

function* getWidgetDD({ value: { widget, options } }) {
  yield delay(200);
  yield put({ type: types.DD_GET_WIDGET_SUCCESS, data: ddsData.items[options.active], widget, });
}

function* getScl({ value: { selectedSourceId, selectedProject, id, language } }) {
  try {
    const res = yield call(() => AnalysisService.getAnalysis(id, {
      project: selectedProject,
      sourceId: selectedSourceId ? [selectedSourceId] : [],
    }));
    if (res.status === 200) {
      // let associatedEvents = [];

      // const sourceId = selectedSourceId !== 'overview' ? selectedSourceId : undefined;

      // const eventsRes = yield call(() => RiskService.getEvents({
      //   id: res.data.subjectDetails.subjectId,
      //   params: { sourceId },
      // }));

      // if (eventsRes) {
      //   associatedEvents = eventsRes.data;
      // }

      const data = {
        widgets: {
          // ...mapScl(res.data, language, associatedEvents),
          ...mapScl(res.data, language),
          subjectId: res.data.subjectDetails.subjectId,
        },
        options: res.data.options,
        sources: res.data.sources || [],
      };
      yield put({
        type: types.SCL_FETCH_SUCCESS,
        ...data,
      });
    } else {
      throw new Error('Error');
    }
  } catch (err) {
    if (isProxy() && sessionStorage.getItem('proxy_project') !== PROJECTS.myData) {
      sessionStorage.setItem('proxy_project', PROJECTS.myData);
      window.location.reload();
      return;
    }
    console.log(err);
  }
}

export default function* analysis() {
  yield takeEvery(types.SCP_FETCH_REQUEST, getScp);
  yield takeEvery(types.SCP_SOURCE_CHANGED_REQEUEST, getScpSources);
  yield takeEvery(types.SCP_GET_WIDGET_REQUEST, getWidgetScp);
  yield takeEvery(types.KYC_FETCH_REQUEST, getKyc);
  yield takeEvery(types.DD_FETCH_REQUEST, getDD);
  yield takeEvery(types.DD_GET_WIDGET_REQUEST, getWidgetDD);
  yield takeEvery(types.SCL_FETCH_REQUEST, getScl);
  yield takeEvery(types.SCL_SOURCE_CHANGED_REQEUEST, getSclSources);
  yield takeEvery(types.MINE_OPERATORS_FETCH_REQUEST, getMineOperators);
  yield takeEvery(types.MINE_OPERATORS_SOURCE_CHANGED_REQEUEST, getMineOperatorsSources);
  yield takeEvery(types.MINE_OPERATORS_GET_WIDGET_REQUEST, getWidgetMineOperators);
  yield takeEvery(types.AMP_FETCH_REQUEST, getAmp);
  yield takeEvery(types.AMP_SOURCE_CHANGED_REQEUEST, getAmpSources);
  yield takeEvery(types.AMP_GET_WIDGET_REQUEST, getWidgetAmp);
  yield takeEvery(types.PROGRAMS_FETCH_REQUEST, getPrograms);
  yield takeEvery(types.PROGRAMS_SOURCE_CHANGED_REQEUEST, getProgramsSources);
  yield takeEvery(types.PROGRAMS_GET_WIDGET_REQUEST, getWidgetPrograms);
}
